import { useEffect, useState } from 'react'
import { api } from '../api/client'

export type FusionConfigState = {
  cabinetNumber: string
  cabinetArea: string
  managers: string[]
}

const empty: FusionConfigState = { cabinetNumber: '', cabinetArea: '', managers: [] }

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'))
      return
    }
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new DOMException('The operation was aborted.', 'AbortError'))
      },
      { once: true },
    )
  })
}

// useFusionConfig loads the shared cabinet and manager list. Opening the page
// cancels the first request (React remounts once in development); that cancel
// must not stick as "网络中断". A real miss is tried again before the page
// tells the operator the list is empty.
export function useFusionConfig() {
  const [state, setState] = useState<FusionConfigState>(empty)
  const [error, setError] = useState('')
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    ;(async () => {
      let last = ''
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const next = await api.get<FusionConfigState>('/api/fusion', ac.signal)
          if (ac.signal.aborted) return
          setState({
            cabinetNumber: next.cabinetNumber ?? '',
            cabinetArea: next.cabinetArea ?? '',
            managers: next.managers ?? [],
          })
          setError('')
          setReady(true)
          return
        } catch (e) {
          if (ac.signal.aborted || (e as Error)?.name === 'AbortError') return
          last = e instanceof Error ? e.message : '没有读到配置'
          if (attempt < 2) {
            try {
              await wait(400 * (attempt + 1), ac.signal)
            } catch {
              return
            }
          }
        }
      }
      if (!ac.signal.aborted) setError(last)
    })()
    return () => ac.abort()
  }, [])

  return { state, error, ready }
}
